import React, { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar as CalendarIcon, Clock, Search, X, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { listRDOs, getRDOs } from "@/services/rdoStorageSupabase";

// Ocorrência real (payload dos RDOs) -> linha de exibição
interface OccurrenceRow {
  id: string;
  title: string;
  date: string; // yyyy-mm-dd
  time: string;
  category: string;
  severity: "low" | "medium" | "high";
  author: string;
  description: string;
}

function impactToSeverity(impact?: string): OccurrenceRow["severity"] {
  const t = (impact ?? "").toLowerCase();
  if (t.includes("alto") || t.includes("alta") || t.includes("grave")) return "high";
  if (t.includes("baix")) return "low";
  return "medium";
}

async function loadOccurrences(obraId?: string): Promise<OccurrenceRow[]> {
  const rdos = obraId ? await listRDOs(obraId) : await getRDOs();
  const rows: OccurrenceRow[] = [];
  for (const rdo of rdos) {
    (rdo.occurrences ?? []).forEach((occ, i) => {
      rows.push({
        id: `${rdo.id}-${i}`,
        title: occ.type || "Ocorrência",
        date: rdo.date,
        time: "",
        category: occ.type || "Geral",
        severity: impactToSeverity(occ.impact),
        author: occ.responsible || "—",
        description: occ.description || "",
      });
    });
  }
  return rows;
}

const severityConfig = {
  low: { label: "Baixa", variant: "outline" as const, color: "text-green-600" },
  medium: { label: "Média", variant: "secondary" as const, color: "text-yellow-600" },
  high: { label: "Alta", variant: "destructive" as const, color: "text-red-600" },
};

interface AllOccurrencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quando informado, lista só as ocorrências dos RDOs desta obra */
  obraId?: string;
}

export function AllOccurrencesModal({ open, onOpenChange, obraId }: AllOccurrencesModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todas");
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Ocorrências reais (payload dos RDOs), carregadas ao abrir
  const [allOccurrences, setAllOccurrences] = useState<OccurrenceRow[]>([]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    loadOccurrences(obraId)
      .then((rows) => { if (active) setAllOccurrences(rows); })
      .catch((e) => console.error("AllOccurrencesModal:", e));
    return () => { active = false; };
  }, [open, obraId]);

  // Categorias derivadas dos dados reais
  const categoryOptions = useMemo(
    () => ["Todas", ...Array.from(new Set(allOccurrences.map((o) => o.category))).sort()],
    [allOccurrences]
  );

  // Função para filtrar ocorrências
  const filteredOccurrences = useMemo(() => {
    let filtered = allOccurrences;

    // Filtro por termo de busca
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(occurrence => 
        occurrence.title.toLowerCase().includes(searchLower) ||
        occurrence.author.toLowerCase().includes(searchLower) ||
        occurrence.description.toLowerCase().includes(searchLower)
      );
    }

    // Filtro por categoria
    if (selectedCategory !== "Todas") {
      filtered = filtered.filter(occurrence => occurrence.category === selectedCategory);
    }

    // Filtro por período
    if (dateRange.from || dateRange.to) {
      filtered = filtered.filter(occurrence => {
        const occurrenceDate = new Date(occurrence.date);
        
        if (dateRange.from && dateRange.to) {
          return occurrenceDate >= dateRange.from && occurrenceDate <= dateRange.to;
        } else if (dateRange.from) {
          return occurrenceDate >= dateRange.from;
        } else if (dateRange.to) {
          return occurrenceDate <= dateRange.to;
        }
        
        return true;
      });
    }

    // Ordenação por data (mais recentes primeiro)
    return [...filtered].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [allOccurrences, searchTerm, selectedCategory, dateRange]);

  // Parse manual (yyyy-mm-dd) para evitar off-by-one de timezone
  const formatDateForDisplay = (dateString: string) => {
    const [y, m, d] = dateString.split("-");
    return `${d}/${m}/${y}`;
  };

  const clearDateRange = () => {
    setDateRange({});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Todas as Ocorrências
          </DialogTitle>
        </DialogHeader>

        {/* Filtros */}
        <div className="flex flex-col gap-4 pb-4 border-b">
          {/* Linha 1: Busca e Categoria */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar ocorrências..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Linha 2: Filtro de Período */}
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Período:</span>
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[280px] justify-start text-left font-normal",
                      !dateRange.from && !dateRange.to && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "dd/MM/yy", { locale: ptBR })} -{" "}
                          {format(dateRange.to, "dd/MM/yy", { locale: ptBR })}
                        </>
                      ) : (
                        format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })
                      )
                    ) : (
                      <span>Selecionar período</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range) => setDateRange(range || {})}
                    numberOfMonths={2}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              
              {(dateRange.from || dateRange.to) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearDateRange}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Lista de Ocorrências */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {filteredOccurrences.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhuma ocorrência encontrada com os filtros aplicados.</p>
            </div>
          ) : (
            filteredOccurrences.map((occurrence) => {
              const severityInfo = severityConfig[occurrence.severity as keyof typeof severityConfig];
              return (
                <Card key={occurrence.id} className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-medium text-foreground">#{occurrence.id}</span>
                        <span className="font-medium text-foreground">{occurrence.title}</span>
                        <span className="text-muted-foreground">{formatDateForDisplay(occurrence.date)}</span>
                        <span className="text-muted-foreground">{occurrence.time}</span>
                        <span className="text-muted-foreground">por {occurrence.author}</span>
                        <Badge variant="outline" className="text-xs">
                          {occurrence.category}
                        </Badge>
                      </div>
                      <Badge variant={severityInfo.variant} className="text-xs">
                        {severityInfo.label}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Rodapé com total */}
        <div className="pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Exibindo {filteredOccurrences.length} de {allOccurrences.length} ocorrências
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}